/**
 * Console Capture Utility
 * Intercepts console.error and console.warn to store them for bug reports
 */

const MAX_LOGS = 50;
const capturedLogs = [];

let isInitialized = false;
let originalConsoleError = null;
let originalConsoleWarn = null;

/**
 * Initialize console capture
 * Call this once at app startup
 */
export function initConsoleCapture() {
    if (isInitialized) return;

    originalConsoleError = console.error;
    originalConsoleWarn = console.warn;

    console.error = (...args) => {
        captureLogs('error', args);
        originalConsoleError.apply(console, args);
    };

    console.warn = (...args) => {
        captureLogs('warn', args);
        originalConsoleWarn.apply(console, args);
    };

    // Capture unhandled errors
    window.addEventListener('error', (event) => {
        captureLogs('error', [
            `Unhandled Error: ${event.message}`,
            `at ${event.filename}:${event.lineno}:${event.colno}`
        ]);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        captureLogs('error', [
            `Unhandled Promise Rejection: ${event.reason}`
        ]);
    });

    isInitialized = true;
}

/**
 * Capture log entry
 */
function captureLogs(type, args) {
    const entry = {
        type,
        timestamp: new Date().toISOString(),
        message: args.map(arg => {
            try {
                if (typeof arg === 'object') {
                    return JSON.stringify(arg, null, 2);
                }
                return String(arg);
            } catch {
                return '[Unable to stringify]';
            }
        }).join(' ')
    };

    capturedLogs.push(entry);

    // Keep only the last MAX_LOGS entries
    if (capturedLogs.length > MAX_LOGS) {
        capturedLogs.shift();
    }
}

/**
 * Get all captured logs
 * @returns {Array} Array of log entries
 */
export function getCapturedLogs() {
    return [...capturedLogs];
}

/**
 * Clear captured logs
 */
export function clearCapturedLogs() {
    capturedLogs.length = 0;
}

/**
 * Get logs formatted for bug report
 * @returns {Array} Formatted log entries
 */
export function getLogsForReport() {
    return capturedLogs.map(log => ({
        type: log.type,
        timestamp: log.timestamp,
        message: log.message.substring(0, 1000) // Limit message length
    }));
}
